import { supabase } from '@/lib/supabase';
import { normalizeError } from '@/lib/errorUtils';
import { CreditCardPurchaseIntentController, type CardIntentStorage } from '@/lib/creditCardPurchaseIntent';
import type { CreditCardEngineInvoice, CreditCardEnginePurchase, CreditCardPurchaseEditScope, UpdateCreditCardPurchaseChanges } from '@/lib/creditCardEngine';

export type CardPurchaseCreatePayload = {
  description: string; category: string; subcategory: string | null; notes: string | null;
  amount: number; purchaseDate: string; invoiceMonth: string | null;
  mode: 'single' | 'installment' | 'repeat'; installments: number; installmentStart: number;
  amountMode: 'total' | 'installment'; transactionType: 'purchase' | 'refund' | 'adjustment';
  originalPurchaseId: string | null; refundReason: string | null;
};
export type CardPurchaseCreateResult = {
  rows: CreditCardEnginePurchase[];
  savedInvoices: CreditCardEngineInvoice[];
  count: number;
  startInstallment: number;
};
export type CardPurchaseMutationResult = {
  sourceCardId?: string;
  targetCardId?: string;
  targets: CreditCardEnginePurchase[];
  affectedInvoiceMonths: string[];
  invoices: CreditCardEngineInvoice[];
  updatedCount: number;
  deletedCount: number;
};

type Rpc = (name: string, args: Record<string, unknown>) => PromiseLike<{ data: unknown; error: unknown }>;
type Envelope = CardPurchaseCreateResult | CardPurchaseMutationResult;

function validateResult(value: unknown, create: boolean): Envelope {
  if (!value || typeof value !== 'object') throw new Error('card_purchase_result_unconfirmed');
  const result = value as Record<string, unknown>;
  const invoices = create ? result.savedInvoices : result.invoices;
  const rows = create ? result.rows : result.targets;
  if (!Array.isArray(invoices) || !Array.isArray(rows) ||
    !invoices.every(i => i && typeof i.id === 'string') ||
    !rows.every(row => row && typeof row.id === 'string') ||
    (create && (!Number.isInteger(result.count) || !Number.isInteger(result.startInstallment))) ||
    (!create && (!Array.isArray(result.affectedInvoiceMonths) || !Number.isInteger(result.deletedCount ?? 0)))) {
    throw new Error('card_purchase_result_unconfirmed');
  }
  return value as Envelope;
}

export function createCardPurchaseCommandService(dependencies: { rpc: Rpc; storage: () => CardIntentStorage }) {
  const controllers = new Map<string, CreditCardPurchaseIntentController<Envelope>>();
  const completed = new WeakMap<Promise<Envelope>, Promise<Envelope>>();

  function execute(userId: string, cardId: string, operation: string, args: Record<string, unknown>, create: boolean) {
    if (!userId || !cardId) return Promise.reject(new Error('card_purchase_identity_required'));
    const scope = `${userId}:${cardId}:${operation}`;
    let controller = controllers.get(scope);
    if (!controller) {
      controller = new CreditCardPurchaseIntentController(scope, dependencies.storage());
      controllers.set(scope, controller);
    }
    // This immutable wire payload is shared by fingerprinting and transport.
    // No client user_id, composed invoice, cycle snapshot or transport clock.
    const wire = JSON.parse(JSON.stringify(args)) as Record<string, unknown>;
    const request = controller.run(wire, async intent => {
      const { data, error } = await dependencies.rpc(create ? 'create_credit_card_purchase_atomic' : 'mutate_credit_card_purchase_atomic', {
        ...wire, p_idempotency_key: intent.idempotencyKey,
      });
      if (error) {
        const failure = normalizeError(error, 'credit_card_purchase_command_failed');
        const sql = error as { code?: unknown; message?: unknown };
        const rolledBack = typeof sql.code === 'string' && /^[0-9A-Z]{5}$/.test(sql.code) &&
          !/idempotency.*conflict/i.test(String(sql.message));
        throw Object.assign(failure, { cardCommandRolledBack: rolledBack });
      }
      return validateResult(data, create);
    });
    const existing = completed.get(request);
    if (existing) return existing;
    const active = controller;
    const result = request.then(value => {
      // Failure to remove the durable key after COMMIT must not turn success
      // into a failed financial operation. Keeping it causes a safe retry.
      try {
        if (active.complete() && controllers.get(scope) === active) controllers.delete(scope);
      } catch { /* Keep the confirmed intent until storage is available again. */ }
      return value;
    }, error => {
      try {
        if (active.resolveFailure(error?.cardCommandRolledBack === true) && controllers.get(scope) === active) {
          controllers.delete(scope);
        }
      } catch { /* Preserve the durable intent when cleanup is unavailable. */ }
      throw error;
    });
    completed.set(request, result);
    return result;
  }

  return {
    create(userId: string, cardId: string, payload: CardPurchaseCreatePayload): Promise<CardPurchaseCreateResult> {
      return execute(userId, cardId, 'create', { p_card_id: cardId, p_payload: payload }, true) as Promise<CardPurchaseCreateResult>;
    },
    mutate(input: {
      userId: string; cardId: string; operation: 'update' | 'delete' | 'sync' | 'delete_card';
      purchaseId?: string; scope?: CreditCardPurchaseEditScope;
      changes?: UpdateCreditCardPurchaseChanges; extraMonths?: string[];
    }): Promise<CardPurchaseMutationResult> {
      return execute(input.userId, input.cardId, `${input.operation}:${input.purchaseId ?? ''}`, {
        p_card_id: input.cardId, p_operation: input.operation,
        p_purchase_id: input.purchaseId ?? null, p_scope: input.scope ?? 'single',
        p_changes: input.changes ?? {}, p_extra_months: Array.from(new Set(input.extraMonths ?? [])).sort(),
      }, false) as Promise<CardPurchaseMutationResult>;
    },
  };
}

export const CreditCardPurchaseCommandService = createCardPurchaseCommandService({
  rpc: (name, args) => supabase.rpc(name, args),
  storage: () => {
    if (typeof window === 'undefined') throw new Error('card_purchase_durable_storage_unavailable');
    return window.sessionStorage;
  },
});
